import { useState } from 'react';
import { toast } from 'sonner';
import { themes, useTheme } from './ThemeProvider';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, Briefcase, GraduationCap, Heart, Search, SlidersHorizontal, Sparkles, Star, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface EnhancedSearchPageProps {
  onBack: () => void;
}

interface Profile {
  id: number;
  name: string;
  age: number;
  height: string;
  education: string;
  profession: string;
  location: string;
  salary: string;
  religion: string;
  caste: string;
  motherTongue: string;
  maritalStatus: string;
  diet: string;
  smoking: string;
  drinking: string;
  image: string;
  verified: boolean;
  premium: boolean;
  aiScore: number;
  interests: string[];
  bio: string;
}

const sampleProfiles: Profile[] = [
  {
    id: 1,
    name: "Priya Sharma",
    age: 26,
    height: "5'5\"",
    education: "MBA",
    profession: "Marketing Manager",
    location: "Mumbai, Maharashtra",
    salary: "₹8-10 LPA",
    religion: "Hindu",
    caste: "Brahmin",
    motherTongue: "Hindi",
    maritalStatus: "Never Married",
    diet: "Vegetarian",
    smoking: "No",
    drinking: "No",
    image: "",
    verified: true,
    premium: true,
    aiScore: 95,
    interests: ["Travel", "Reading", "Yoga", "Music"],
    bio: "Looking for a life partner who values family and career equally."
  },
  {
    id: 2,
    name: "Amit Patel",
    age: 29,
    height: "5'10\"",
    education: "B.Tech, IIT Delhi",
    profession: "Software Engineer",
    location: "Bangalore, Karnataka",
    salary: "₹15-20 LPA",
    religion: "Hindu",
    caste: "Patel",
    motherTongue: "Gujarati",
    maritalStatus: "Never Married",
    diet: "Vegetarian",
    smoking: "No",
    drinking: "Occasionally",
    image: "",
    verified: true,
    premium: false,
    aiScore: 88,
    interests: ["Technology", "Cricket", "Travel", "Photography"],
    bio: "Passionate about technology and looking for someone with similar interests."
  },
  {
    id: 3,
    name: "Sneha Reddy",
    age: 25,
    height: "5'4\"",
    education: "MBBS",
    profession: "Doctor",
    location: "Hyderabad, Telangana",
    salary: "₹10-12 LPA",
    religion: "Hindu",
    caste: "Reddy",
    motherTongue: "Telugu",
    maritalStatus: "Never Married",
    diet: "Non-Vegetarian",
    smoking: "No",
    drinking: "No",
    image: "",
    verified: true,
    premium: true,
    aiScore: 92,
    interests: ["Medicine", "Dance", "Cooking", "Fitness"],
    bio: "Dedicated doctor seeking a supportive and understanding partner."
  },
  {
    id: 4,
    name: "Rahul Singh",
    age: 28,
    height: "5'11\"",
    education: "CA",
    profession: "Chartered Accountant",
    location: "Delhi, India",
    salary: "₹12-15 LPA",
    religion: "Hindu",
    caste: "Rajput",
    motherTongue: "Hindi",
    maritalStatus: "Never Married",
    diet: "Non-Vegetarian",
    smoking: "No",
    drinking: "Socially",
    image: "",
    verified: false,
    premium: false,
    aiScore: 82,
    interests: ["Finance", "Cricket", "Music", "Movies"],
    bio: "Family-oriented person looking for a caring and loving partner."
  }
];

export default function EnhancedSearchPage({ onBack }: EnhancedSearchPageProps) {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  
  const [searchMode, setSearchMode] = useState<'ai' | 'advanced'>('ai');
  const [aiQuery, setAiQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<Profile[]>([]);
  
  // Advanced filters
  const [ageRange, setAgeRange] = useState([21, 35]);
  const [heightRange, setHeightRange] = useState([150, 185]);
  const [selectedLocation, setSelectedLocation] = useState('any');
  const [selectedEducation, setSelectedEducation] = useState('any');
  const [selectedProfession, setSelectedProfession] = useState('any');
  const [selectedReligion, setSelectedReligion] = useState('any');
  const [selectedMaritalStatus, setSelectedMaritalStatus] = useState('any');
  const [selectedDiet, setSelectedDiet] = useState('any');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [premiumOnly, setPremiumOnly] = useState(false);

  const handleAISearch = () => {
    if (!aiQuery.trim()) {
      toast.error('Please describe what you\'re looking for');
      return;
    }

    setIsSearching(true);
    // Simulate AI processing
    setTimeout(() => {
      const filtered = sampleProfiles.sort((a, b) => b.aiScore - a.aiScore);
      setResults(filtered);
      setIsSearching(false);
      toast.success(`Found ${filtered.length} AI-matched profiles based on your preferences!`);
    }, 1500);
  };

  const handleAdvancedSearch = () => {
    setIsSearching(true);
    setTimeout(() => {
      let filtered = sampleProfiles.filter(profile => {
        const ageMatch = profile.age >= ageRange[0] && profile.age <= ageRange[1];
        const locationMatch = selectedLocation === 'any' || profile.location.includes(selectedLocation);
        const educationMatch = selectedEducation === 'any' || profile.education.toLowerCase().includes(selectedEducation.toLowerCase());
        const religionMatch = selectedReligion === 'any' || profile.religion === selectedReligion;
        const maritalMatch = selectedMaritalStatus === 'any' || profile.maritalStatus === selectedMaritalStatus;
        const dietMatch = selectedDiet === 'any' || profile.diet === selectedDiet;
        const verifiedMatch = !verifiedOnly || profile.verified;
        const premiumMatch = !premiumOnly || profile.premium;

        return ageMatch && locationMatch && educationMatch && religionMatch && 
               maritalMatch && dietMatch && verifiedMatch && premiumMatch;
      });

      setResults(filtered);
      setIsSearching(false);
      toast.success(`Found ${filtered.length} matching profiles!`);
    }, 1000);
  };

  const clearFilters = () => {
    setAgeRange([21, 35]);
    setHeightRange([150, 185]);
    setSelectedLocation('any');
    setSelectedEducation('any');
    setSelectedProfession('any');
    setSelectedReligion('any');
    setSelectedMaritalStatus('any');
    setSelectedDiet('any');
    setVerifiedOnly(false);
    setPremiumOnly(false);
    setResults([]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="size-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className={`size-6 ${currentTheme.primaryText}`} />
              <span className={`text-xl font-bold ${currentTheme.primaryText}`}>Smart Search</span>
            </div>
            <div className="w-20" />
          </div>

          {/* Search Mode Toggle */}
          <Tabs value={searchMode} onValueChange={(v) => setSearchMode(v as 'ai' | 'advanced')}>
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
              <TabsTrigger value="ai" className="gap-2">
                <Sparkles className="size-4" />
                AI Search
              </TabsTrigger>
              <TabsTrigger value="advanced" className="gap-2">
                <SlidersHorizontal className="size-4" />
                Advanced
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* AI Search */}
        {searchMode === 'ai' && (
          <div className="max-w-3xl mx-auto">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className={`size-5 ${currentTheme.primaryText}`} />
                  AI-Powered Match Finder
                </CardTitle>
                <CardDescription>
                  Describe your ideal partner in natural language and let our AI find the perfect matches
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Sparkles className={`absolute left-3 top-3 size-5 ${currentTheme.primaryText}`} />
                  <textarea
                    placeholder="Example: Looking for a well-educated professional from Mumbai who loves traveling, is family-oriented, vegetarian, between 25-30 years old, and shares similar values..."
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    className="w-full min-h-[120px] pl-12 pr-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-rose-500"
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleAISearch}
                    disabled={isSearching}
                    className={`flex-1 ${currentTheme.gradient} text-white`}
                  >
                    {isSearching ? (
                      <>
                        <div className="animate-spin size-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        AI is analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="size-4 mr-2" />
                        Find Matches with AI
                      </>
                    )}
                  </Button>
                  {aiQuery && (
                    <Button variant="outline" onClick={() => setAiQuery('')}>
                      <X className="size-4" />
                    </Button>
                  )}
                </div>

                {/* AI Suggestions */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">Quick AI Prompts:</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "IT professional from Bangalore, vegetarian, 28-32",
                      "Doctor, family-oriented, loves travel",
                      "MBA graduate, Mumbai/Delhi, non-smoker",
                      "Traditional values, homemaker preferred"
                    ].map((prompt, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        onClick={() => setAiQuery(prompt)}
                      >
                        {prompt}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights */}
            {results.length > 0 && (
              <Card className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="size-5 text-purple-600" />
                    AI Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Badge className="bg-purple-600 text-white">Smart Match</Badge>
                      <p className="text-sm">
                        Based on your description, we've prioritized profiles with {results[0]?.aiScore}% compatibility scores.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Badge className="bg-blue-600 text-white">Tip</Badge>
                      <p className="text-sm">
                        The top matches share similar interests and lifestyle preferences as mentioned in your search.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Advanced Search */}
        {searchMode === 'advanced' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Filters</CardTitle>
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Age Range */}
                  <div className="space-y-3">
                    <Label>Age: {ageRange[0]} - {ageRange[1]} years</Label>
                    <Slider
                      min={18}
                      max={60}
                      step={1}
                      value={ageRange}
                      onValueChange={setAgeRange}
                    />
                  </div>

                  {/* Height Range */}
                  <div className="space-y-3">
                    <Label>Height: {heightRange[0]} - {heightRange[1]} cm</Label>
                    <Slider
                      min={140}
                      max={200}
                      step={1}
                      value={heightRange}
                      onValueChange={setHeightRange}
                    />
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Location</SelectItem>
                        <SelectItem value="Mumbai">Mumbai</SelectItem>
                        <SelectItem value="Delhi">Delhi</SelectItem>
                        <SelectItem value="Bangalore">Bangalore</SelectItem>
                        <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                        <SelectItem value="Chennai">Chennai</SelectItem>
                        <SelectItem value="Pune">Pune</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Education */}
                  <div className="space-y-2">
                    <Label>Education</Label>
                    <Select value={selectedEducation} onValueChange={setSelectedEducation}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Education</SelectItem>
                        <SelectItem value="B.Tech">B.Tech/BE</SelectItem>
                        <SelectItem value="MBA">MBA</SelectItem>
                        <SelectItem value="MBBS">MBBS/MD</SelectItem>
                        <SelectItem value="CA">CA</SelectItem>
                        <SelectItem value="Graduate">Graduate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Religion */}
                  <div className="space-y-2">
                    <Label>Religion</Label>
                    <Select value={selectedReligion} onValueChange={setSelectedReligion}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Religion</SelectItem>
                        <SelectItem value="Hindu">Hindu</SelectItem>
                        <SelectItem value="Muslim">Muslim</SelectItem>
                        <SelectItem value="Christian">Christian</SelectItem>
                        <SelectItem value="Sikh">Sikh</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Marital Status */}
                  <div className="space-y-2">
                    <Label>Marital Status</Label>
                    <Select value={selectedMaritalStatus} onValueChange={setSelectedMaritalStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="Never Married">Never Married</SelectItem>
                        <SelectItem value="Divorced">Divorced</SelectItem>
                        <SelectItem value="Widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Diet */}
                  <div className="space-y-2">
                    <Label>Diet</Label>
                    <Select value={selectedDiet} onValueChange={setSelectedDiet}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="Vegetarian">Vegetarian</SelectItem>
                        <SelectItem value="Non-Vegetarian">Non-Vegetarian</SelectItem>
                        <SelectItem value="Vegan">Vegan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Special Filters */}
                  <div className="space-y-3 pt-3 border-t">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="verified" 
                        checked={verifiedOnly}
                        onCheckedChange={(checked) => setVerifiedOnly(checked as boolean)}
                      />
                      <Label htmlFor="verified">Verified Profiles Only</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="premium" 
                        checked={premiumOnly}
                        onCheckedChange={(checked) => setPremiumOnly(checked as boolean)}
                      />
                      <Label htmlFor="premium">Premium Members Only</Label>
                    </div>
                  </div>

                  <Button 
                    onClick={handleAdvancedSearch}
                    disabled={isSearching}
                    className={`w-full ${currentTheme.gradient} text-white`}
                  >
                    {isSearching ? 'Searching...' : 'Apply Filters'}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              {results.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent className="pt-6">
                    <Search className="size-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Ready to Find Your Match?</h3>
                    <p className="text-gray-500">
                      Use the filters on the left to search for your perfect partner
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      {results.length} {results.length === 1 ? 'Match' : 'Matches'} Found
                    </h3>
                    <Select defaultValue="aiScore">
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aiScore">Best Match</SelectItem>
                        <SelectItem value="recent">Recently Active</SelectItem>
                        <SelectItem value="verified">Verified First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {results.map(profile => (
                      <Card key={profile.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="relative">
                          <ImageWithFallback 
                            src={profile.image} 
                            alt={profile.name}
                            className="w-full h-64 object-cover"
                          />
                          {profile.premium && (
                            <Badge className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                              <Star className="size-3 mr-1" />
                              Premium
                            </Badge>
                          )}
                          {profile.verified && (
                            <Badge className="absolute top-3 right-3 bg-blue-600 text-white">
                              ✓ Verified
                            </Badge>
                          )}
                          {searchMode === 'ai' && (
                            <Badge className={`absolute bottom-3 right-3 ${currentTheme.gradient} text-white`}>
                              <Sparkles className="size-3 mr-1" />
                              {profile.aiScore}% Match
                            </Badge>
                          )}
                        </div>

                        <CardHeader>
                          <CardTitle>{profile.name}, {profile.age}</CardTitle>
                          <CardDescription>{profile.height} • {profile.profession}</CardDescription>
                        </CardHeader>

                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="size-4" />
                            {profile.location}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <GraduationCap className="size-4" />
                            {profile.education}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Briefcase className="size-4" />
                            {profile.salary}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{profile.bio}</p>
                          
                          <div className="flex flex-wrap gap-2">
                            {profile.interests.slice(0, 3).map(interest => (
                              <Badge key={interest} variant="outline" className="text-xs">
                                {interest}
                              </Badge>
                            ))}
                          </div>
                        </CardContent>

                        <CardFooter className="border-t pt-4">
                          <div className="flex gap-2 w-full">
                            <Button variant="outline" className="flex-1">
                              <Heart className="size-4 mr-2" />
                              Interest
                            </Button>
                            <Button className={`flex-1 ${currentTheme.gradient} text-white`}>
                              View Profile
                            </Button>
                          </div>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}